import { noop, isFunction } from "lodash";

var config = {};
config.GameManager={
  // If continueGameTrigger set to `true`, a game will stop after each move
  // until `continueGameTrigger()` is called in console or UI
  continueGameOnTrigger: false,
  moveLog: false,
  newStateLog: false
};

function logState() {}

/**
 * @class
 * GameManager is an object that represents a particular game,
 *   incapsulates its current state and manage each move of each subject.
 * GameManager is initialized by Game object and an initial state.
 * Each subject must be represented with a player who must be registered
 *   via GameManager.register method.
 * The only centralized behavious GameManager has is start/stop/resume
 * @param {Game} game - a {@link Game} object
 * @param {Object} initialState - a state to start with
 * @alias module:GameManager
 * @alias GameManager
 * @memberof module:GameManager
 */
function GameManager(game, initialState) {
  
  /** @private */
  var state = initialState;
  
  /**
   * an object whose keys are subjets and
   * values are move notification callbacks.
   * @private
   */
  var players = {};
  
  /**
   * curForm and curReaction always correspond to `game.getFormReaction(state)`
   *   when it is not undefined.
   * @private
   */
  
  var curForm;
  /**
   * curForm and curReaction always correspond to `game.getFormReaction(state)`
   *   when it is not undefined.
   * @private
   */
  var curReaction;
  
  /**
   * onMove event handler called after each move.
   * @param form
   * @return new state
   * @private
   */
  var onMove = noop;
  
  /**
   * onUnknownSubj event handler called when a subject has no implementation.
   * @private
   */
  var onUnknownSubj = noop;
  
  /**
   * A boolean value used to distinguish to stages:
   *   1. onMove, getFormReaction etc are processing and no player is active,
   *   2. there is an active player.
   * 
   * Using DB terms, one may say that a move is an atomic action.
   * This flag is important when interstate transition
   *   (i.e. calculating the next state, form and reaction) takes a time
   *   and asyncronous functions might be executed during that stage.
   * In that case GameManager must prevent another {@link GameManager~makeMove}
   *   call causing unsupposed side effects
   *   (e.g. doubled {@link GameManager~onMove} events or unwanted move)
   * @private
   */
  var intermediatestage = true;
  
  
  /**
   * Register a player given by an object.
   * Possible fields are:
   * - subj - a string identification
   * - notify - a callback which is called when 'subj' must take a move
   *
   * @param playerObject -
   *   an object with mandatory 'subj' and 'notify'
   * @return a callback player will use to notify Game object about his move
   */
  this.register = function(playerObject) {
    var subj = playerObject.subj;
    if(players[playerObject.subj]) {
      throw "Cannot register " + subj + " as it has already been registered";
    }
    players[subj] = playerObject.notify;
    /**
     * Continuation callback.
     * A player calls it when a move is made.
     * It calles {@link GameManager~makeMove} if the game is not in
     *   an intermediate stage and subject the player is assigned to
     *   is the one who is supposed to make a move.
     * @param v - an option (i.e. an object identifying player's move)
     * @return undefined
     */
    return function makeMoveCallback(v) {
      if(config.GameManager.moveLog) {
        console.log("Receiving move callback from " + subj);
        if(state.subj != subj)
          console.log("Ignoring, current player is " + state.subj);
      }
      if(state.subj != subj) return; // preventing moves out of order
      if(intermediatestage) return;  // preventing moves when
                                     //   new state is being calculated
      intermediatestage = true;
      setTimeout(function() {
        makeMove(v);
        if(config.GameManager.continueGameOnTrigger) {
          window.continueGameTrigger = function() {
            setTimeout(continueGame, 0);
            delete window.continueGameTrigger;
          };
        } else {
          setTimeout(continueGame, 0);
        }
      });
    }
  };
  
  /**
   * add a handler that is called when move is made.
   * A handler is a function of an object consisting of:
   * - `state` - the currect state
   * - `form` - the form that was given to a player
   * - `v` - the option player has chosen
   * - `r` - the next state
   * {@link GameManager~onMove}
   * @param {Function} handler - unary callback
   * @return undefined
   */
  this.onMove = function(handler) {
    if(isFunction(handler)) {
      var onMove1 = onMove;
      onMove = function(arg) {
        onMove1(arg);
        handler(arg);
      };
    }
  };
  
  /**
   * add a handler that is called when the game is finished.
   * @param {Function} callback - a function of no argument
   *   that causes side effects
   * @return undefined
   */
  this.onFinish = function(callback) {
    if(isFunction(callback)) {
      if(onFinish.callbacklist === undefined)
        onFinish.callbacklist = [];
      onFinish.callbacklist.push(callback);
    } else {
      console.error("callback is not a function");
    }
  };
  
  /**
   * onFinish event handler called when the game is finished.
   * (i.e. when `!game.getFormReaction(state)`)
   * @private
   */
  function onFinish() {
    if(onFinish.callbacklist === undefined) throw "callbacklist undefined";
    onFinish.callbacklist.forEach(function(callback) { callback(state); });
  }
  
  /**
   * replace a state with a new one.
   * This function is called right after a player made his move.
   * @param {Object} v - an option (i.e. an object identifying player's move)
   * @private
   */
  function makeMove(v) {
    if(config.GameManager.moveLog) {
      console.group('reaction');
      var t0 = performance.now();
      var r = curReaction(v);
      var t1 = performance.now();
      console.log('Next state calculated in ' + Math.floor(t1 - t0) + ' milisec');
      if (t1 - t0 > 1000) {
        console.log(v);
      }
      console.groupEnd();
    } else {
      var r = curReaction(v);
    }
    onMove({ r: r, v: v, state: state, form: curForm });
    state = r;
    if(config.GameManager.newStateLog) {
      console.log("New state:");
      logState(state);
    }
  }

  /**
   * This procedure starts new round
   * @private
   */
  function continueGame() {
    // try {
    if(config.GameManager.moveLog) {
      console.log("%cSTARTING NEW ROUND", "font-size: 14px; font-weight: bold;");
      logState(state);
      console.log(state);
    }
    var player = players[state.subj];
    if(player) { // in case
      if (config.GameManager.moveLog) {
        console.group('getFormReact');
        var t0 = performance.now();
        var fr = game.getFormReaction(state);
        var t1 = performance.now();
        console.log('getFormReaction finished in ' + Math.floor(t1 - t0) + ' milisec');
        console.groupEnd();
      } else {
        var fr = game.getFormReaction(state);
      }
      if(!fr) {
        onFinish(state);
        return;
      }
      curForm = fr.form;
      curReaction = fr.reaction;
      if(config.GameManager.moveLog) {
        console.log("Current form: ", curForm);
        if(curForm.type == "EmptyForm") {
          console.warn("The form is empty");
          console.log(curForm);
          console.log(player);
          console.log("Terminating…");
          return;
        }
      }
      intermediatestage = false;
      config.GameManager.moveLog && console.log("Waiting 10 milisec before a player gets a form");
      setTimeout(function() { player(curForm) }, 10);
    }
    else {
      console.warn("Undefined player");
      logState(state);
      console.log(players);
      return;
      continueGame();
    }
    // } catch(e) { console.log(e); throw "stop!"; }
  }
  
  /**
   * start the game.
   * This function should not be called more than once.
   */
  this.start = function() { continueGame(); };

  /**
   * clone a GameManager object with its game and state.
   * Event handlers (onMove, onFinish) are not preserved.
   * Use this method to reset/reassign event handlers.
   * @return a new instance of GameManager
   */
  this.clone = function() {
    var GM = new GameManager(game, state);
    return GM;
  }
  
  /**
   * a direct reference to a game object.
   * @deprecated
   */
  this.game = game;

}

export default GameManager;
